import React, { useEffect, useState, useMemo } from 'react';
import {
  Stack,
  Text,
  PrimaryButton,
  DefaultButton,
  Spinner,
  MessageBar,
  MessageBarType,
  DocumentCard,
  DocumentCardDetails,
  DocumentCardTitle,
  DocumentCardType,
  Icon,
  SearchBox,
  TooltipHost,
  Selection,
  SelectionMode,
  CommandBar,
  ICommandBarItemProps,
  Checkbox,
  IconButton,
  Dialog,
  DialogType,
  DialogFooter,
  Dropdown,
  IDropdownOption,
} from '@fluentui/react';
import { useNavigate } from 'react-router-dom';
import { IBestellungMitPositionen, IUserPermissions, BestellStatus } from '../../shared/models';
import { bestellungenApi, approvalFlowApi } from '../services/api';
import { BestellungFormular } from '../components/BestellungFormular';
import { HelpTooltip } from '../components/HelpTooltip';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

// Badge-Farben (gleiche Logik wie TabBadge)
const BADGE_COLORS = {
  gray:   { bg: '#d0d0d0', text: '#555' },
  green:  { bg: '#107c10', text: '#fff' },
  yellow: { bg: '#d4960a', text: '#fff' },
  red:    { bg: '#c50f1f', text: '#fff' },
};
function tileBadgeColor(total: number, unread: number, hasMention: boolean) {
  if (total === 0) return BADGE_COLORS.gray;
  if (unread === 0) return BADGE_COLORS.green;
  if (hasMention) return BADGE_COLORS.red;
  return BADGE_COLORS.yellow;
}
interface ITileBadgeData {
  kommentare: { total: number; unread: number; hasMention: boolean };
  dokumente:  { total: number; unread: number };
}

// Filter-Typen
type FilterType = 'alle' | 'meine' | 'zuBearbeiten';

// Interface für ausstehende Aufgaben
interface IPendingTasks {
  pendingApprovals: number[];  // BestellungIds wo Benutzer genehmigen muss
  unansweredMentions: number[]; // BestellungIds mit unbeantworteten @-Mentions
}

const getGenehmigungsstatusColor = (status: string): string => {
  switch (status) {
    case 'Freigegeben':
      return '#107c10'; // Grün
    case 'Teilweise freigegeben':
      return '#0078d4'; // Blau
    case 'Offen':
      return '#ffaa44'; // Orange
    case 'Abgelehnt':
      return '#d13438'; // Rot
    case 's. Position':
      return '#0078d4'; // Blau
    default:
      return '#605e5c'; // Grau
  }
};

const getBestellstatusColor = (status: string): string => {
  switch (status) {
    case 'Geliefert':
      return '#107c10'; // Dunkelgrün
    case 'Teilweise geliefert':
      return '#5db85d'; // Hellgrün
    case 'Bestellt':
      return '#ffaa44'; // Orange
    case 'Eingereicht':
      return '#d13438'; // Rot
    case 's. Position':
      return '#0078d4'; // Blau
    default:
      return '#605e5c'; // Grau
  }
};

export const BestellungenListe: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bestellungen, setBestellungen] = useState<IBestellungMitPositionen[]>([]);
  const [filteredBestellungen, setFilteredBestellungen] = useState<IBestellungMitPositionen[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterType>('alle');
  const [selectedItems, setSelectedItems] = useState<IBestellungMitPositionen[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [bestellungFormOpen, setBestellungFormOpen] = useState(false);
  const [permissions, setPermissions] = useState<IUserPermissions | null>(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [pendingTasks, setPendingTasks] = useState<IPendingTasks>({ pendingApprovals: [], unansweredMentions: [] });
  const [archiveDialogVisible, setArchiveDialogVisible] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [approveDialogVisible, setApproveDialogVisible] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejectDialogVisible, setRejectDialogVisible] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [bestellstatusDialogVisible, setBestellstatusDialogVisible] = useState(false);
  const [changingBestellstatus, setChangingBestellstatus] = useState(false);
  const [selectedBestellstatus, setSelectedBestellstatus] = useState<BestellStatus>(BestellStatus.Bestellt);
  const [flowsEntfernenDialogVisible, setFlowsEntfernenDialogVisible] = useState(false);
  const [entferningFlows, setEntferningFlows] = useState(false);
  const [bcBatchDialogVisible, setBcBatchDialogVisible] = useState(false);
  const [bcBatching, setBcBatching] = useState(false);
  const [bcBatchResults, setBcBatchResults] = useState<{ bestellungId: number; title: string; erfolg: boolean; fehler?: string }[]>([]);
  const [bcBatchResultsDialogVisible, setBcBatchResultsDialogVisible] = useState(false);
  const [tileBadges, setTileBadges] = useState<Record<number, ITileBadgeData>>({});

  useEffect(() => {
    loadBestellungen();
    loadPermissions();
    loadPendingTasks();
  }, []);

  useEffect(() => {
    filterBestellungen();
  }, [searchText, activeFilter, bestellungen, permissions, pendingTasks]);

  // Lade Benutzerberechtigungen
  const loadPermissions = async () => {
    try {
      if (user?.email) {
        const response = await axios.get('/api/permissions/me', {
          params: { email: user.email }
        });
        setPermissions(response.data);
        setPermissionsLoaded(true);
      } else {
        setPermissionsLoaded(true);
      }
    } catch (err: any) {
      console.error('Fehler beim Laden der Berechtigungen:', err?.response?.data || err.message || err);
      setPermissionsLoaded(true);
    }
  };

  // Lade ausstehende Aufgaben (Genehmigungen + Mentions)
  const loadPendingTasks = async () => {
    try {
      // Verwende Email direkt aus validiertem MSAL Account (wie bei loadPermissions)
      if (user?.email) {
        const response = await axios.get('/api/bestellungen/pending-tasks', {
          params: { email: user.email }
        });
        setPendingTasks(response.data);
      }
    } catch (err) {
      console.error('Fehler beim Laden der ausstehenden Aufgaben:', err);
    }
  };

  // Prüfe ob Benutzer Admin-Gruppe hat (DGS-Einkauf, DGS-Admin, DGS-Geschäftsführer)
  const isAdminUser = useMemo(() => {
    if (!permissions) return false;
    return permissions.isAdmin || permissions.isEinkauf || permissions.isGeschaeftsfuehrer;
  }, [permissions]);

  const loadBestellungen = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await bestellungenApi.getAll();
      setBestellungen(data);
      // Badges asynchron nachladen (kein Blocking)
      const ids = data.map((b: IBestellungMitPositionen) => b.Id).filter(Boolean) as number[];
      if (ids.length) {
        axios.get(`/api/read-status/bestellungen?ids=${ids.join(',')}`)
          .then(r => setTileBadges(r.data))
          .catch(() => {});
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Bestellungen');
    } finally {
      setLoading(false);
    }
  };

  const filterBestellungen = () => {
    let filtered = [...bestellungen];
    const userEmail = user?.email?.toLowerCase() || '';
    // Extrahiere displayName aus Account (Format: "Vorname Nachname" oder "Nachname, Vorname")
    const userName = user?.displayName || '';

    // Erstelle beide Namensformate für Vergleich
    let userNameNormal = userName; // "Vorname Nachname"
    let userNameReversed = userName; // "Nachname, Vorname"

    if (userName.includes(',')) {
      // Name ist bereits im Format "Nachname, Vorname"
      userNameReversed = userName;
      const parts = userName.split(',').map(p => p.trim());
      if (parts.length >= 2) {
        userNameNormal = `${parts[1]} ${parts[0]}`; // "Vorname Nachname"
      }
    } else if (userName.includes(' ')) {
      // Name ist im Format "Vorname Nachname"
      userNameNormal = userName;
      const parts = userName.split(' ');
      if (parts.length >= 2) {
        userNameReversed = `${parts[parts.length - 1]}, ${parts.slice(0, -1).join(' ')}`; // "Nachname, Vorname"
      }
    }

    // Textsuche (erweitert um zusätzliche Felder)
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((b) => {
        // Suche in Bestellungsfeldern
        const bestellungMatch =
          b.Title?.toLowerCase().includes(searchLower) ||
          b.InterneBestellnummer?.toLowerCase().includes(searchLower) ||
          b.AnforderndePersonen?.toLowerCase().includes(searchLower) ||
          b.AnforderndeKostenstelle?.toLowerCase().includes(searchLower) ||
          b.Personalnummer?.toLowerCase().includes(searchLower);

        // Suche in Positionsfeldern
        const positionMatch = b.Positionen?.some(
          (p) =>
            p.EmpfangendeKostenstelle?.toLowerCase().includes(searchLower) ||
            p.Artikelname?.toLowerCase().includes(searchLower) ||
            p.Artikelnummer?.toLowerCase().includes(searchLower) ||
            p.BCNummer?.toLowerCase().includes(searchLower) ||
            p.Sachkonto?.toLowerCase().includes(searchLower) ||
            p.InventarAnlagennummer?.toLowerCase().includes(searchLower)
        );

        return bestellungMatch || positionMatch;
      });
    }

    // Wenn Berechtigungen noch nicht geladen sind, zeige KEINE Bestellungen
    // (Sicherheit: Nutzer darf erst Bestellungen sehen, wenn Berechtigungen validiert sind)
    if (!permissionsLoaded) {
      setFilteredBestellungen([]);
      return;
    }

    // Hilfsfunktion: Prüft ob Benutzer Ersteller/Anfordernde Person ist
    const isUserOwner = (b: IBestellungMitPositionen): boolean => {
      const authorEmail = b.Author?.Email?.toLowerCase() || '';
      const authorTitle = b.Author?.Title?.toLowerCase() || '';
      const anfordernde = b.AnforderndePersonen?.toLowerCase() || '';

      // Prüfe Email-Match
      if (authorEmail && authorEmail === userEmail) return true;

      // Prüfe Name-Match (Author.Title) - beide Formate prüfen
      if (authorTitle) {
        if (authorTitle === userNameNormal.toLowerCase()) return true;
        if (authorTitle === userNameReversed.toLowerCase()) return true;
      }

      // Prüfe AnforderndePersonen (enthält oft "Nachname, Vorname")
      if (anfordernde) {
        if (anfordernde.includes(userNameNormal.toLowerCase())) return true;
        if (anfordernde.includes(userNameReversed.toLowerCase())) return true;
        if (anfordernde.includes(userEmail)) return true;
      }

      return false;
    };

    // Filter basierend auf aktivem Button
    switch (activeFilter) {
      case 'meine':
        // Bestellungen wo Benutzer Author/Creator oder Anfordernde Person ist
        filtered = filtered.filter(isUserOwner);
        break;

      case 'zuBearbeiten':
        // Bestellungen mit ausstehenden Genehmigungen oder unbeantworteten @-Mentions
        filtered = filtered.filter((b) => {
          const hasPendingApproval = pendingTasks.pendingApprovals.includes(b.Id || 0);
          const hasUnansweredMention = pendingTasks.unansweredMentions.includes(b.Id || 0);
          return hasPendingApproval || hasUnansweredMention;
        });
        break;

      case 'alle':
      default:
        // Wenn keine Berechtigungen geladen werden konnten, zeige KEINE Bestellungen
        if (!permissions) {
          filtered = [];
        }
        // Server filtert vertrauliche Bestellungen auf SQL-Ebene
        // Alle vom Server zurückgegebenen Bestellungen dürfen angezeigt werden
        break;
    }

    setFilteredBestellungen(filtered);
  };

  const onBestellungClick = (bestellung: IBestellungMitPositionen) => {
    if (!selectionMode && bestellung.Id) {
      navigate(`/bestellungen/${bestellung.Id}`);
    }
  };

  const onEditClick = (e: React.MouseEvent<any>, bestellung: IBestellungMitPositionen) => {
    e.stopPropagation();
    if (bestellung.Id) {
      navigate(`/bestellungen/${bestellung.Id}`);
    }
  };

  const toggleSelection = (bestellung: IBestellungMitPositionen) => {
    if (selectedItems.some(item => item.Id === bestellung.Id)) {
      setSelectedItems(selectedItems.filter(item => item.Id !== bestellung.Id));
    } else {
      setSelectedItems([...selectedItems, bestellung]);
    }
  };

  const isSelected = (bestellung: IBestellungMitPositionen): boolean => {
    return selectedItems.some(item => item.Id === bestellung.Id);
  };

  const handleArchiveSelected = async () => {
    if (selectedItems.length === 0) return;

    setArchiving(true);
    try {
      for (const bestellung of selectedItems) {
        if (bestellung.Id) {
          await axios.post(`/api/bestellungen/${bestellung.Id}/archive`);
        }
      }
      setArchiveDialogVisible(false);
      setSelectedItems([]);
      setSelectionMode(false);
      await loadBestellungen();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Fehler beim Archivieren');
    } finally {
      setArchiving(false);
    }
  };

  const handleApproveSelected = async () => {
    if (selectedItems.length === 0) return;

    setApproving(true);
    try {
      for (const bestellung of selectedItems) {
        if (bestellung.Id && bestellung.AnforderndeKostenstelle) {
          // Erstelle Freigabe-Flow für die Bestellung
          await approvalFlowApi.createFlowForBestellung(
            bestellung.Id,
            bestellung.AnforderndeKostenstelle,
            user?.email
          );
        }
      }
      setApproveDialogVisible(false);
      setSelectedItems([]);
      setSelectionMode(false);
      await loadBestellungen();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Fehler beim Freigeben');
    } finally {
      setApproving(false);
    }
  };

  const handleRejectSelected = async () => {
    if (selectedItems.length === 0 || !user?.email) return;

    setRejecting(true);
    try {
      for (const bestellung of selectedItems) {
        if (bestellung.Id) {
          // Lade alle Flows für diese Bestellung
          const flows = await approvalFlowApi.getFlowsForBestellung(bestellung.Id);
          // Lehne jeden Flow ab
          for (const flow of flows) {
            if (flow.Id) {
              await approvalFlowApi.rejectStep(flow.Id, user.email);
            }
          }
        }
      }
      setRejectDialogVisible(false);
      setSelectedItems([]);
      setSelectionMode(false);
      await loadBestellungen();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Fehler beim Ablehnen');
    } finally {
      setRejecting(false);
    }
  };

  const handleEntferneFlows = async () => {
    if (selectedItems.length === 0) return;

    setEntferningFlows(true);
    try {
      for (const bestellung of selectedItems) {
        if (bestellung.Id) {
          const flows = await approvalFlowApi.getFlowsForBestellung(bestellung.Id);
          for (const flow of flows) {
            if (flow.Id) {
              await approvalFlowApi.deleteFlow(flow.Id);
            }
          }
        }
      }
      setFlowsEntfernenDialogVisible(false);
      setSelectedItems([]);
      setSelectionMode(false);
      await loadBestellungen();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Fehler beim Entfernen der Genehmigungsflows');
    } finally {
      setEntferningFlows(false);
    }
  };

  const handleBcBatchSelected = async () => {
    if (selectedItems.length === 0) return;
    setBcBatching(true);
    const results: { bestellungId: number; title: string; erfolg: boolean; fehler?: string }[] = [];
    try {
      for (const bestellung of selectedItems) {
        if (bestellung.Id) {
          try {
            const response = await axios.post(`/api/bc/${bestellung.Id}`);
            const ergebnisse = response.data?.ergebnisse || [];
            const allOk = ergebnisse.every((e: any) => e.erfolg);
            results.push({
              bestellungId: bestellung.Id,
              title: bestellung.Title || String(bestellung.Id),
              erfolg: allOk,
              fehler: allOk ? undefined : ergebnisse.filter((e: any) => !e.erfolg).map((e: any) => e.fehler).join('; '),
            });
          } catch (err: any) {
            results.push({
              bestellungId: bestellung.Id,
              title: bestellung.Title || String(bestellung.Id),
              erfolg: false,
              fehler: err.response?.data?.error || err.message,
            });
          }
        }
      }
      setBcBatchDialogVisible(false);
      setBcBatchResults(results);
      setBcBatchResultsDialogVisible(true);
      setSelectedItems([]);
      setSelectionMode(false);
      await loadBestellungen();
    } finally {
      setBcBatching(false);
    }
  };

  const handleChangeBestellstatusSelected = async () => {
    if (selectedItems.length === 0) return;

    setChangingBestellstatus(true);
    try {
      for (const bestellung of selectedItems) {
        if (bestellung.Id && bestellung.Positionen?.length > 0) {
          for (const position of bestellung.Positionen) {
            if (position.PosID) {
              await axios.patch(`/api/bestellungen/${bestellung.Id}/positionen/${position.PosID}`, {
                Bestellstatus: selectedBestellstatus,
              });
            }
          }
        }
      }
      setBestellstatusDialogVisible(false);
      setSelectedItems([]);
      setSelectionMode(false);
      await loadBestellungen();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Fehler beim Ändern des Bestellstatus');
    } finally {
      setChangingBestellstatus(false);
    }
  };

  // CommandBar-Aktionen
  const commandBarItems: ICommandBarItemProps[] = useMemo(() => {
    const items: ICommandBarItemProps[] = [];

    // "Neue Bestellung" Button
    items.push({
      key: 'new',
      text: 'Neue Bestellung',
      iconProps: { iconName: 'Add' },
      onClick: () => setBestellungFormOpen(true),
    });

    // "Auswählen" Button
    items.push({
      key: 'select',
      text: selectionMode ? 'Auswahl beenden' : 'Auswählen',
      iconProps: { iconName: selectionMode ? 'Cancel' : 'MultiSelect' },
      onClick: () => {
        setSelectionMode(!selectionMode);
        setSelectedItems([]);
      },
    });

    // "Archivieren" Button (nur wenn Elemente ausgewählt)
    if (selectionMode && selectedItems.length > 0 && permissions?.canArchive) {
      items.push({
        key: 'archive',
        text: `Archivieren (${selectedItems.length})`,
        iconProps: { iconName: 'Archive' },
        onClick: () => setArchiveDialogVisible(true),
      });
    }

    // "Genehmigungsflows" Submenu (Admin/Einkauf): Freigeben + Ablehnen + Entfernen
    if (selectionMode && selectedItems.length > 0 && (permissions?.isAdmin || permissions?.isEinkauf)) {
      items.push({
        key: 'genehmigungsflows',
        text: `Genehmigungsflows (${selectedItems.length})`,
        iconProps: { iconName: 'WorkFlow' },
        subMenuProps: {
          items: [
            {
              key: 'flows-freigeben',
              text: 'Freigeben',
              iconProps: { iconName: 'Play' },
              onClick: () => setApproveDialogVisible(true),
            },
            {
              key: 'flows-ablehnen',
              text: 'Ablehnen',
              iconProps: { iconName: 'Cancel' },
              onClick: () => setRejectDialogVisible(true),
            },
            {
              key: 'flows-entfernen',
              text: 'Entfernen',
              iconProps: { iconName: 'Delete' },
              onClick: () => setFlowsEntfernenDialogVisible(true),
            },
          ],
        },
      });
    }

    // "Freigeben" Button (für canApprove ohne Admin/Einkauf, z.B. GF und Freigeben-Rolle)
    if (selectionMode && selectedItems.length > 0 && permissions?.canApprove && !permissions?.isAdmin && !permissions?.isEinkauf) {
      items.push({
        key: 'approve',
        text: `Freigeben (${selectedItems.length})`,
        iconProps: { iconName: 'CheckMark' },
        onClick: () => setApproveDialogVisible(true),
      });
    }

    // "Ablehnen" Button (nur für canApprove ohne Admin/Einkauf)
    if (selectionMode && selectedItems.length > 0 && permissions?.canApprove && !permissions?.isAdmin && !permissions?.isEinkauf) {
      items.push({
        key: 'reject',
        text: `Ablehnen (${selectedItems.length})`,
        iconProps: { iconName: 'Cancel' },
        onClick: () => setRejectDialogVisible(true),
      });
    }

    // "Bestellstatus ändern" Button (nur Admin und Einkauf)
    if (selectionMode && selectedItems.length > 0 && (permissions?.isAdmin || permissions?.isEinkauf)) {
      items.push({
        key: 'changeBestellstatus',
        text: `Bestellstatus ändern (${selectedItems.length})`,
        iconProps: { iconName: 'Sync' },
        onClick: () => setBestellstatusDialogVisible(true),
      });
    }

    // "In BC anlegen" Button (nur Admin und Einkauf)
    if (selectionMode && selectedItems.length > 0 && (permissions?.isAdmin || permissions?.isEinkauf)) {
      items.push({
        key: 'bcAnlegen',
        text: `In BC anlegen (${selectedItems.length})`,
        iconProps: { iconName: 'PublishContent' },
        onClick: () => setBcBatchDialogVisible(true),
      });
    }

    return items;
  }, [selectedItems, permissions, selectionMode]);

  // Zähler für Filter-Badges
  const meineBestellungenCount = useMemo(() => {
    const userEmail = user?.email?.toLowerCase() || '';
    const userName = user?.displayName || '';

    // Erstelle beide Namensformate
    let userNameNormal = userName;
    let userNameReversed = userName;

    if (userName.includes(',')) {
      userNameReversed = userName;
      const parts = userName.split(',').map(p => p.trim());
      if (parts.length >= 2) {
        userNameNormal = `${parts[1]} ${parts[0]}`;
      }
    } else if (userName.includes(' ')) {
      userNameNormal = userName;
      const parts = userName.split(' ');
      if (parts.length >= 2) {
        userNameReversed = `${parts[parts.length - 1]}, ${parts.slice(0, -1).join(' ')}`;
      }
    }

    return bestellungen.filter((b) => {
      const authorEmail = b.Author?.Email?.toLowerCase() || '';
      const authorTitle = b.Author?.Title?.toLowerCase() || '';
      const anfordernde = b.AnforderndePersonen?.toLowerCase() || '';

      // Email-Match
      if (authorEmail && authorEmail === userEmail) return true;
      // Name-Match
      if (authorTitle === userNameNormal.toLowerCase() || authorTitle === userNameReversed.toLowerCase()) return true;
      // AnforderndePersonen-Match
      if (anfordernde.includes(userNameNormal.toLowerCase()) || anfordernde.includes(userNameReversed.toLowerCase())) return true;
      if (anfordernde.includes(userEmail)) return true;

      return false;
    }).length;
  }, [bestellungen, user]);

  const zuBearbeitenCount = useMemo(() => {
    return bestellungen.filter((b) => {
      const hasPendingApproval = pendingTasks.pendingApprovals.includes(b.Id || 0);
      const hasUnansweredMention = pendingTasks.unansweredMentions.includes(b.Id || 0);
      return hasPendingApproval || hasUnansweredMention;
    }).length;
  }, [bestellungen, pendingTasks]);

  return (
    <Stack tokens={{ childrenGap: 20 }}>
      {/* Header */}
      <Text variant="xxLarge" styles={{ root: { fontWeight: 600 } }}>
        Bestellungen
      </Text>

      {/* CommandBar mit allen Aktionen */}
      <CommandBar
        items={commandBarItems}
        styles={{
          root: {
            backgroundColor: '#f3f2f1',
            padding: 0,
          },
        }}
      />

      {/* Filter & Suche */}
      <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center" wrap>
        <HelpTooltip helpText="Durchsucht alle Bestellungen nach Titel, Bestellnummer, Antragsteller, Kostenstelle und Artikeldetails (Name, Nr., Bögra-Nr., Sachkonto, Inventarnummer).">
          <SearchBox
            placeholder="Suchen..."
            value={searchText}
            onChange={(_, newValue) => setSearchText(newValue || '')}
            styles={{ root: { width: 300 } }}
          />
        </HelpTooltip>

        {/* Filter-Buttons mit Notification-Badges */}
        {/* Meine Bestellungen */}
        <div style={{ position: 'relative' }}>
          <TooltipHost content="Meine Bestellungen - Bestellungen, bei denen ich Ersteller oder anfordernde Person bin">
            <IconButton
              iconProps={{ iconName: 'Contact' }}
              checked={activeFilter === 'meine'}
              onClick={() => setActiveFilter('meine')}
              styles={{
                root: {
                  backgroundColor: activeFilter === 'meine' ? '#0078d4' : 'transparent',
                  color: activeFilter === 'meine' ? 'white' : '#323130',
                  border: '1px solid #8a8886',
                  borderRadius: 4,
                  width: 36,
                  height: 36,
                },
                rootHovered: {
                  backgroundColor: activeFilter === 'meine' ? '#106ebe' : '#f3f2f1',
                },
                rootChecked: {
                  backgroundColor: '#0078d4',
                  color: 'white',
                },
              }}
            />
          </TooltipHost>
          {meineBestellungenCount > 0 && (
            <div
              style={{
                position: 'absolute',
                top: -4,
                right: -4,
                backgroundColor: '#0078d4',
                color: 'white',
                borderRadius: '50%',
                minWidth: 18,
                height: 18,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                fontWeight: 600,
                padding: '0 4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                border: '2px solid white',
              }}
            >
              {meineBestellungenCount}
            </div>
          )}
        </div>

        {/* Zu bearbeiten */}
        <div style={{ position: 'relative' }}>
          <TooltipHost content="Zu bearbeiten - Bestellungen mit ausstehenden Genehmigungen oder unbeantworteten @-Erwähnungen">
            <IconButton
              iconProps={{ iconName: 'Taskboard' }}
              checked={activeFilter === 'zuBearbeiten'}
              onClick={() => setActiveFilter('zuBearbeiten')}
              styles={{
                root: {
                  backgroundColor: activeFilter === 'zuBearbeiten' ? '#0078d4' : 'transparent',
                  color: activeFilter === 'zuBearbeiten' ? 'white' : '#323130',
                  border: '1px solid #8a8886',
                  borderRadius: 4,
                  width: 36,
                  height: 36,
                },
                rootHovered: {
                  backgroundColor: activeFilter === 'zuBearbeiten' ? '#106ebe' : '#f3f2f1',
                },
                rootChecked: {
                  backgroundColor: '#0078d4',
                  color: 'white',
                },
              }}
            />
          </TooltipHost>
          {zuBearbeitenCount > 0 && (
            <div
              style={{
                position: 'absolute',
                top: -4,
                right: -4,
                backgroundColor: '#d13438',
                color: 'white',
                borderRadius: '50%',
                minWidth: 18,
                height: 18,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                fontWeight: 600,
                padding: '0 4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                border: '2px solid white',
              }}
            >
              {zuBearbeitenCount}
            </div>
          )}
        </div>

        {/* Alle Bestellungen */}
        <div style={{ position: 'relative' }}>
          <TooltipHost content={isAdminUser
            ? "Alle Bestellungen - Zeigt alle Bestellungen an"
            : "Alle Bestellungen - Zeigt meine Bestellungen und solche, die ich bearbeiten muss"}>
            <IconButton
              iconProps={{ iconName: 'ViewAll' }}
              checked={activeFilter === 'alle'}
              onClick={() => setActiveFilter('alle')}
              styles={{
                root: {
                  backgroundColor: activeFilter === 'alle' ? '#0078d4' : 'transparent',
                  color: activeFilter === 'alle' ? 'white' : '#323130',
                  border: '1px solid #8a8886',
                  borderRadius: 4,
                  width: 36,
                  height: 36,
                },
                rootHovered: {
                  backgroundColor: activeFilter === 'alle' ? '#106ebe' : '#f3f2f1',
                },
                rootChecked: {
                  backgroundColor: '#0078d4',
                  color: 'white',
                },
              }}
            />
          </TooltipHost>
        </div>
      </Stack>

      {/* Error Message */}
      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}

      {/* Loading Spinner */}
      {loading ? (
        <Stack horizontalAlign="center" tokens={{ padding: 40 }}>
          <Spinner label="Lade Bestellungen..." />
        </Stack>
      ) : (
        <>
          {/* Statistik */}
          <Stack horizontal tokens={{ childrenGap: 16 }}>
            <Stack
              styles={{
                root: {
                  padding: '12px 16px',
                  backgroundColor: 'white',
                  borderLeft: '3px solid #0078d4',
                  boxShadow: '0 1.6px 3.6px 0 rgba(0,0,0,.132), 0 0.3px 0.9px 0 rgba(0,0,0,.108)',
                  minWidth: 120,
                },
              }}
            >
              <Text variant="large" styles={{ root: { fontWeight: 600, color: '#323130' } }}>
                {filteredBestellungen.length}
              </Text>
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                Bestellungen
              </Text>
            </Stack>
            {selectionMode && selectedItems.length > 0 && (
              <Stack
                styles={{
                  root: {
                    padding: '12px 16px',
                    backgroundColor: '#deecf9',
                    borderLeft: '3px solid #0078d4',
                    boxShadow: '0 1.6px 3.6px 0 rgba(0,0,0,.132), 0 0.3px 0.9px 0 rgba(0,0,0,.108)',
                    minWidth: 120,
                  },
                }}
              >
                <Text variant="large" styles={{ root: { fontWeight: 600, color: '#0078d4' } }}>
                  {selectedItems.length}
                </Text>
                <Text variant="small" styles={{ root: { color: '#0078d4' } }}>
                  Ausgewählt
                </Text>
              </Stack>
            )}
          </Stack>

          {/* Kachelansicht */}
          {filteredBestellungen.length === 0 ? (
            <Stack horizontalAlign="center" tokens={{ padding: 40 }}>
              <Icon iconName="DocumentSet" styles={{ root: { fontSize: 48, color: '#c8c6c4' } }} />
              <Text variant="large" styles={{ root: { marginTop: 16, color: '#605e5c' } }}>
                Keine Bestellungen gefunden
              </Text>
            </Stack>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                gap: '16px',
              }}
            >
              {filteredBestellungen.map((bestellung) => {
                const selected = isSelected(bestellung);
                const genehmigungsstatus = bestellung.Genehmigungsstatus || 'Offen';
                const bestellstatus = bestellung.Bestellstatus || 'Eingereicht';
                const ebNummern = [...new Set((bestellung.Positionen || []).filter(p => p.BCBestellnummer).map(p => p.BCBestellnummer!))];
                const ebNummer = ebNummern.length === 0 ? null : ebNummern.length === 1 ? ebNummern[0] : 's. Position';

                return (
                  <DocumentCard
                    key={bestellung.Id}
                    type={DocumentCardType.compact}
                    onClick={() => onBestellungClick(bestellung)}
                    styles={{
                      root: {
                        cursor: 'pointer',
                        border: selected ? '2px solid #0078d4' : '1px solid #edebe9',
                        backgroundColor: selected ? '#f3f9fd' : 'white',
                        boxShadow: selected
                          ? '0 3.2px 7.2px 0 rgba(0,0,0,.132), 0 0.6px 1.8px 0 rgba(0,0,0,.108)'
                          : '0 1.6px 3.6px 0 rgba(0,0,0,.132), 0 0.3px 0.9px 0 rgba(0,0,0,.108)',
                        '&:hover': {
                          borderColor: '#0078d4',
                          boxShadow: '0 3.2px 7.2px 0 rgba(0,0,0,.132), 0 0.6px 1.8px 0 rgba(0,0,0,.108)',
                        },
                        transition: 'all 0.1s ease',
                        minHeight: '235px',
                        position: 'relative',
                      },
                    }}
                  >
                    <DocumentCardDetails
                      styles={{
                        root: {
                          padding: '16px',
                        },
                      }}
                    >
                      {/* Edit Icon (top left) */}
                      <div style={{ position: 'absolute', top: 8, left: 8 }}>
                        <IconButton
                          iconProps={{ iconName: 'Edit' }}
                          title="Bestellung bearbeiten"
                          onClick={(e) => onEditClick(e, bestellung)}
                          styles={{
                            root: {
                              width: 28,
                              height: 28,
                              color: '#605e5c',
                            },
                            rootHovered: {
                              color: '#0078d4',
                              backgroundColor: '#f3f2f1',
                            },
                          }}
                        />
                      </div>

                      {/* Checkbox (top right) */}
                      {selectionMode && (
                        <div style={{ position: 'absolute', top: 8, right: 8 }}>
                          <Checkbox
                            checked={selected}
                            onChange={(e) => {
                              e?.stopPropagation();
                              toggleSelection(bestellung);
                            }}
                          />
                        </div>
                      )}

                      {/* Badge-Indikatoren (Kommentare + Dokumente) – oben rechts */}
                      {bestellung.Id && (() => {
                        const badge = tileBadges[bestellung.Id!];
                        if (!badge) return null;
                        const kColor = tileBadgeColor(badge.kommentare.total, badge.kommentare.unread, badge.kommentare.hasMention);
                        const dColor = tileBadgeColor(badge.dokumente.total, badge.dokumente.unread, false);
                        return (
                          <div style={{
                            position: 'absolute',
                            top: 8,
                            right: selectionMode ? 36 : 8,
                            display: 'flex',
                            gap: 4,
                            alignItems: 'center',
                          }}>
                            <TooltipHost content={
                              badge.kommentare.total === 0 ? 'Keine Kommentare'
                              : badge.kommentare.unread === 0 ? `${badge.kommentare.total} Kommentar(e), alle gelesen`
                              : badge.kommentare.hasMention ? `${badge.kommentare.unread} ungelesen (inkl. @Erwähnung)`
                              : `${badge.kommentare.unread} Kommentar(e) ungelesen`
                            }>
                              <div style={{
                                display: 'inline-flex', alignItems: 'center', gap: 3,
                                background: kColor.bg, color: kColor.text,
                                borderRadius: 10, padding: '1px 6px', fontSize: 11, fontWeight: 600,
                                cursor: 'default',
                              }} onClick={e => e.stopPropagation()}>
                                <Icon iconName="Comment" styles={{ root: { fontSize: 10 } }} />
                                {badge.kommentare.total}
                              </div>
                            </TooltipHost>
                            <TooltipHost content={
                              badge.dokumente.total === 0 ? 'Keine Dokumente'
                              : badge.dokumente.unread === 0 ? `${badge.dokumente.total} Dokument(e), alle gelesen`
                              : `${badge.dokumente.unread} Dokument(e) ungelesen`
                            }>
                              <div style={{
                                display: 'inline-flex', alignItems: 'center', gap: 3,
                                background: dColor.bg, color: dColor.text,
                                borderRadius: 10, padding: '1px 6px', fontSize: 11, fontWeight: 600,
                                cursor: 'default',
                              }} onClick={e => e.stopPropagation()}>
                                <Icon iconName="Attach" styles={{ root: { fontSize: 10 } }} />
                                {badge.dokumente.total}
                              </div>
                            </TooltipHost>
                          </div>
                        );
                      })()}

                      {/* Header mit Titel */}
                      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} styles={{ root: { paddingLeft: 36, paddingRight: selectionMode ? 36 : 0 } }}>
                        <Text
                          variant="medium"
                          styles={{
                            root: {
                              fontWeight: 600,
                              color: '#323130',
                            },
                          }}
                        >
                          {bestellung.Title || 'Kein Titel'}
                        </Text>
                        {bestellung.Vertraulich && (
                          <TooltipHost content="Vertrauliche Bestellung">
                            <Icon iconName="Lock" styles={{ root: { color: '#d13438', fontSize: 14 } }} />
                          </TooltipHost>
                        )}
                      </Stack>

                      {/* Status mit Ampel */}
                      <Stack tokens={{ childrenGap: 4 }} styles={{ root: { paddingTop: '8px' } }}>
                        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                          <Text variant="small" styles={{ root: { color: '#605e5c', minWidth: '80px' } }}>
                            Genehmigt:
                          </Text>
                          <TooltipHost content={genehmigungsstatus}>
                            <div
                              style={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                backgroundColor: getGenehmigungsstatusColor(genehmigungsstatus),
                                border: '1px solid #605e5c',
                              }}
                            />
                          </TooltipHost>
                        </Stack>
                        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                          <Text variant="small" styles={{ root: { color: '#605e5c', minWidth: '80px' } }}>
                            Status:
                          </Text>
                          <TooltipHost content={bestellstatus}>
                            <div
                              style={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                backgroundColor: getBestellstatusColor(bestellstatus),
                                border: '1px solid #605e5c',
                              }}
                            />
                          </TooltipHost>
                        </Stack>
                      </Stack>

                      {/* Summe und Liefertermin */}
                      <Stack horizontal tokens={{ childrenGap: 16 }} styles={{ root: { paddingTop: '8px' } }}>
                        <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
                          <Icon iconName="Money" styles={{ root: { fontSize: 14, color: '#0078d4' } }} />
                          <Text
                            variant="medium"
                            styles={{
                              root: {
                                fontWeight: 600,
                                color: '#0078d4',
                              },
                            }}
                          >
                            {new Intl.NumberFormat('de-DE', {
                              style: 'currency',
                              currency: 'EUR',
                            }).format(bestellung.GesamtSumme || 0)}
                          </Text>
                        </Stack>
                        {bestellung.Liefertermin && bestellung.Liefertermin !== 's. Position' && (
                          <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
                            <Icon iconName="DeliveryTruck" styles={{ root: { fontSize: 14, color: '#605e5c' } }} />
                            <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                              KW {bestellung.Liefertermin}
                            </Text>
                          </Stack>
                        )}
                      </Stack>

                      {/* Metadata */}
                      <Stack tokens={{ childrenGap: 6 }} styles={{ root: { paddingTop: '12px' } }}>
                        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                          <Icon iconName="Contact" styles={{ root: { fontSize: 14, color: '#605e5c' } }} />
                          <Text
                            variant="small"
                            styles={{
                              root: {
                                color: '#605e5c',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              },
                            }}
                          >
                            {bestellung.AnforderndePersonen || 'Unbekannt'}
                          </Text>
                        </Stack>

                        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                          <Icon iconName="Calendar" styles={{ root: { fontSize: 14, color: '#605e5c' } }} />
                          <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                            {bestellung.Erstellungsdatum
                              ? new Date(bestellung.Erstellungsdatum).toLocaleDateString('de-DE', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                })
                              : 'Kein Datum'}
                          </Text>
                        </Stack>

                        {bestellung.AnforderndeKostenstelle && (
                          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                            <Icon iconName="PaymentCard" styles={{ root: { fontSize: 14, color: '#605e5c' } }} />
                            <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                              KST: {bestellung.AnforderndeKostenstelle}
                            </Text>
                          </Stack>
                        )}
                        {ebNummer && (
                          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                            <Icon iconName="DocumentManagement" styles={{ root: { fontSize: 14, color: '#605e5c' } }} />
                            <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                              EB: {ebNummer}
                            </Text>
                          </Stack>
                        )}
                      </Stack>
                    </DocumentCardDetails>
                  </DocumentCard>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Archivieren Dialog */}
      <Dialog
        hidden={!archiveDialogVisible}
        onDismiss={() => setArchiveDialogVisible(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Bestellungen archivieren',
          subText: `Möchten Sie ${selectedItems.length} Bestellung${selectedItems.length !== 1 ? 'en' : ''} wirklich archivieren? Die Bestellungen werden ins Archiv verschoben.`,
        }}
      >
        <DialogFooter>
          <PrimaryButton onClick={handleArchiveSelected} text="Archivieren" disabled={archiving} />
          <DefaultButton onClick={() => setArchiveDialogVisible(false)} text="Abbrechen" disabled={archiving} />
        </DialogFooter>
      </Dialog>

      {/* Freigeben Dialog */}
      <Dialog
        hidden={!approveDialogVisible}
        onDismiss={() => setApproveDialogVisible(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Bestellungen freigeben',
          subText: `Möchten Sie für ${selectedItems.length} Bestellung${selectedItems.length !== 1 ? 'en' : ''} den Freigabe-Workflow starten? Es werden automatisch Freigabe-Flows basierend auf der Freigabe-Matrix erstellt.`,
        }}
      >
        <DialogFooter>
          <PrimaryButton onClick={handleApproveSelected} text="Freigeben" disabled={approving} />
          <DefaultButton onClick={() => setApproveDialogVisible(false)} text="Abbrechen" disabled={approving} />
        </DialogFooter>
      </Dialog>

      {/* Ablehnen Dialog */}
      <Dialog
        hidden={!rejectDialogVisible}
        onDismiss={() => setRejectDialogVisible(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Bestellungen ablehnen',
          subText: `Möchten Sie ${selectedItems.length} Bestellung${selectedItems.length !== 1 ? 'en' : ''} wirklich ablehnen? Alle zugehörigen Freigabe-Flows werden abgelehnt. Diese Aktion kann nicht rückgängig gemacht werden.`,
        }}
      >
        <DialogFooter>
          <PrimaryButton onClick={handleRejectSelected} text="Ablehnen" disabled={rejecting} />
          <DefaultButton onClick={() => setRejectDialogVisible(false)} text="Abbrechen" disabled={rejecting} />
        </DialogFooter>
      </Dialog>

      {/* Genehmigungsflows entfernen Dialog */}
      <Dialog
        hidden={!flowsEntfernenDialogVisible}
        onDismiss={() => setFlowsEntfernenDialogVisible(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Genehmigungsflows entfernen',
          subText: `Möchten Sie alle Genehmigungsflows der ${selectedItems.length} ausgewählten Bestellung${selectedItems.length !== 1 ? 'en' : ''} wirklich entfernen? Die Flows werden vollständig gelöscht und der Genehmigungsstatus zurückgesetzt.`,
        }}
      >
        <DialogFooter>
          <PrimaryButton onClick={handleEntferneFlows} text="Entfernen" disabled={entferningFlows} />
          <DefaultButton onClick={() => setFlowsEntfernenDialogVisible(false)} text="Abbrechen" disabled={entferningFlows} />
        </DialogFooter>
      </Dialog>

      {/* Bestellstatus ändern Dialog */}
      <Dialog
        hidden={!bestellstatusDialogVisible}
        onDismiss={() => setBestellstatusDialogVisible(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Bestellstatus ändern',
          subText: `Neuen Bestellstatus für alle Positionen der ${selectedItems.length} ausgewählten Bestellung${selectedItems.length !== 1 ? 'en' : ''} setzen:`,
        }}
        minWidth={360}
      >
        <Dropdown
          label="Neuer Bestellstatus"
          selectedKey={selectedBestellstatus}
          options={[
            { key: BestellStatus.Eingereicht, text: BestellStatus.Eingereicht },
            { key: BestellStatus.Bestellt, text: BestellStatus.Bestellt },
            { key: BestellStatus.Geliefert, text: BestellStatus.Geliefert },
          ]}
          onChange={(_, option) => option && setSelectedBestellstatus(option.key as BestellStatus)}
          styles={{ root: { marginBottom: 16 } }}
        />
        <DialogFooter>
          <PrimaryButton onClick={handleChangeBestellstatusSelected} text="Übernehmen" disabled={changingBestellstatus} />
          <DefaultButton onClick={() => setBestellstatusDialogVisible(false)} text="Abbrechen" disabled={changingBestellstatus} />
        </DialogFooter>
      </Dialog>

      {/* In BC anlegen - Bestätigung */}
      <Dialog
        hidden={!bcBatchDialogVisible}
        onDismiss={() => setBcBatchDialogVisible(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'In BC anlegen',
          subText: `${selectedItems.length} Bestellung${selectedItems.length !== 1 ? 'en' : ''} in Business Central anlegen? Voraussetzung: alle Positionen müssen freigegeben sein.`,
        }}
      >
        <DialogFooter>
          <PrimaryButton onClick={handleBcBatchSelected} text="Anlegen" disabled={bcBatching} />
          <DefaultButton onClick={() => setBcBatchDialogVisible(false)} text="Abbrechen" disabled={bcBatching} />
        </DialogFooter>
      </Dialog>

      {/* In BC anlegen - Ergebnisse */}
      <Dialog
        hidden={!bcBatchResultsDialogVisible}
        onDismiss={() => setBcBatchResultsDialogVisible(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'BC-Anlage – Ergebnisse',
        }}
        minWidth={420}
      >
        <Stack tokens={{ childrenGap: 8 }} styles={{ root: { marginBottom: 16 } }}>
          {bcBatchResults.map((r) => (
            <Stack key={r.bestellungId} horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
              <Icon
                iconName={r.erfolg ? 'CheckMark' : 'Error'}
                styles={{ root: { color: r.erfolg ? '#107c10' : '#d13438', fontSize: 16, flexShrink: 0 } }}
              />
              <Text variant="small">
                <strong>{r.title}:</strong> {r.erfolg ? 'Erfolgreich' : (r.fehler || 'Fehler')}
              </Text>
            </Stack>
          ))}
        </Stack>
        <DialogFooter>
          <PrimaryButton onClick={() => setBcBatchResultsDialogVisible(false)} text="Schließen" />
        </DialogFooter>
      </Dialog>

      {/* Bestellungs-Formular */}
      <BestellungFormular
        isOpen={bestellungFormOpen}
        onDismiss={() => setBestellungFormOpen(false)}
        onSave={(newBestellungId?: number) => {
          loadBestellungen();
          setBestellungFormOpen(false);

          // Wenn eine neue Bestellung erstellt wurde, öffne sie direkt
          if (newBestellungId) {
            navigate(`/bestellungen/${newBestellungId}`);
          }
        }}
      />
    </Stack>
  );
};
